import React from 'react';
import { formatDaySpan, getCalendarDaysUntil } from '../../utils/dateUtils';

/**
 * Badge showing distance until (or since) a target date.
 * Used for employment end date, payment due, etc.
 *
 * Label uses formatDaySpan (DS distance-to-a-date). Colour thresholds stay
 * in raw days — those are urgency buckets (7 / 14), not the display unit.
 *
 * @param {string} dateStr - ISO date string (YYYY-MM-DD)
 * @param {boolean} [isProcessed=false] - When true, past dates use muted styling
 * @param {string} [className] - Extra classes
 */
export const DaysUntilBadge = ({ dateStr, isProcessed = false, className = '' }) => {
  const days = getCalendarDaysUntil(dateStr);
  if (days === null) return null;

  let label;
  let badgeClass;

  if (days > 0) {
    label = formatDaySpan(days, 'long');
    if (days <= 7) {
      badgeClass = 'bg-red-50 text-red-700 border border-red-200';
    } else if (days <= 14) {
      badgeClass = 'bg-amber-50 text-amber-700 border border-amber-200';
    } else {
      badgeClass = 'bg-gray-100 text-gray-700 border border-gray-200';
    }
  } else if (days === 0) {
    label = 'Today';
    badgeClass = 'bg-red-50 text-red-700 border border-red-200';
  } else {
    label = `${formatDaySpan(Math.abs(days), 'long')} ago`;
    badgeClass = isProcessed
      ? 'bg-gray-100 text-gray-500 border border-gray-200'
      : 'bg-amber-50 text-amber-700 border border-amber-200';
  }

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badgeClass} ${className}`.trim()}
      title={days >= 0 ? `${label} until employment end` : `${label}`}
      aria-label={days >= 0 ? `${label} until employment end` : `${label}`}
    >
      {label}
    </span>
  );
};

export default DaysUntilBadge;
